import { optionExistsById } from '@/utils/utils';
import clsx from 'clsx';
import DebouncedInput from 'components/common/DebouncedInput';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { useRef, useEffect } from 'react';
import { EOptionStaticIds } from '@hubertai/screening-question-types';
import { ECreateQuestionMode, useQuestionEditorContext } from '../../context/create-question.context';

import {
  addStaticOptionAction,
  removeOptionById,
  updateMaxSelection,
  updateMinSelection,
} from 'rtk/Question.Editor.Slice';
import {
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectQuestionMaxSelection,
  selectQuestionMinSelection,
  selectQuestionMode,
  selectQuestionOptions,
  selectTemplateMaxSelection,
  selectTemplateMinSelection,
} from 'rtk/questionEditorSelector';
import UneditableInfoTooltip from '../../UneditableInfoTooltip';
import { updateCustomParametersInTemplate } from 'rtk/questionEditorThunks';
import { useTemplateId } from 'hooks/useTemplate';
import { useTranslation } from 'react-i18next';

const CreateOptionOptions = () => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const templateId = useTemplateId();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const options = useAppSelector((state) => selectQuestionOptions(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const templateMin = useAppSelector((state) => selectTemplateMinSelection(state, questionId));
  const templateMax = useAppSelector((state) => selectTemplateMaxSelection(state, questionId));
  const questionMin = useAppSelector((state) => selectQuestionMinSelection(state, questionId));
  const questionMax = useAppSelector((state) => selectQuestionMaxSelection(state, questionId));
  const updatedQuestion = useAppSelector((state) => state.Question.updateQuestion);
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const { t } = useTranslation('Questionsv2');

  const isTemplateEdit = mode === ECreateQuestionMode.InTemplateEdit;
  const originalQuestionMin = useRef<number | undefined>(questionMin);
  const originalQuestionMax = useRef<number | undefined>(questionMax);

  // Sync question state with template values on component mount/update in template edit mode
  useEffect(() => {
    if (isTemplateEdit) {
      if (templateMin !== undefined && templateMin !== questionMin) {
        dispatch(updateMinSelection({ questionId, value: templateMin }));
      }
      if (templateMax !== undefined && templateMax !== questionMax) {
        dispatch(updateMaxSelection({ questionId, value: templateMax }));
      }
    }
  }, [isTemplateEdit, templateMin, templateMax, questionMin, questionMax, questionId, dispatch]);

  // Update placeholder baseline only when question was explicitly saved.
  useEffect(() => {
    if (!updatedQuestion) return;
    if (updatedQuestion.id !== questionId) return;
    const min = (updatedQuestion as { min_selections?: number }).min_selections;
    const max = (updatedQuestion as { max_selections?: number }).max_selections;
    originalQuestionMin.current = min;
    originalQuestionMax.current = max;
  }, [updatedQuestion, questionId]);

  const minInputValue = isTemplateEdit ? (templateMin === undefined ? '' : templateMin) : (questionMin ?? '');
  const maxInputValue = isTemplateEdit ? (templateMax === undefined ? '' : templateMax) : (questionMax ?? '');

  // TODO(i18n): Category 5 — placeholderMin/placeholderMax are ternaries that embed dynamic values
  // inside template literals; they cannot be extracted as-is. Restructure into full t() calls
  // with interpolation (e.g. t('selects.placeholderQuestionDefault', { value: ... })) in a
  // follow-up commit.
  const placeholderMin =
    isTemplateEdit && originalQuestionMin.current !== undefined
      ? `Question default: ${originalQuestionMin.current}`
      : 'Choose minimum number';
  const placeholderMax =
    isTemplateEdit && originalQuestionMax.current !== undefined
      ? `Question default: ${originalQuestionMax.current}`
      : 'Choose maximum number';

  const parseNumberOrUndefined = (val: string | number | null | undefined): number | undefined => {
    if (val === '' || val === undefined || val === null) return undefined;
    const num = typeof val === 'number' ? val : parseInt(val, 10);
    return isNaN(num) ? undefined : num;
  };

  const handleMinChange = (val: string | number) => {
    const value = parseNumberOrUndefined(val);
    const currentValue = isTemplateEdit ? templateMin : questionMin;
    if (value === currentValue) return;
    if (value !== undefined) {
      dispatch(updateMinSelection({ questionId, value }));
    }
    updateCustomParametersInTemplate(
      templateId,
      questionId,
      dispatch,
      mode,
      value,
      'min_selections',
      value === undefined
        ? `User cleared min_selection of ${questionId}.`
        : `User updated min_selection of ${questionId} to ${value}.`,
    );
  };

  const handleMaxChange = (val: string | number) => {
    const value = parseNumberOrUndefined(val);
    const currentValue = isTemplateEdit ? templateMax : questionMax;
    if (value === currentValue) return;
    if (value !== undefined) {
      dispatch(updateMaxSelection({ questionId, value }));
    }
    updateCustomParametersInTemplate(
      templateId,
      questionId,
      dispatch,
      mode,
      value,
      'max_selections',
      value === undefined
        ? `User cleared max_selection of ${questionId}.`
        : `User updated max_selection of ${questionId} to ${value}.`,
    );
  };

  return (
    <div className={clsx('flex w-full flex-col justify-between gap-6')}>
      <div className={clsx('hubert-vertical-center flex flex-row justify-between px-4')}>
        <UneditableInfoTooltip disabled={isTemplateEdit}>
          <HubertSwitch
            id="create-question-option-switch-all-above"
            disabled={isTemplateEdit}
            checked={optionExistsById(options ?? [], 'all-above')}
            onCheckedChange={(e) => {
              if (e) {
                dispatch(
                  addStaticOptionAction({ questionId, id: EOptionStaticIds.AllAbove, language: questionLanguage }),
                );
              } else {
                dispatch(removeOptionById({ questionId, id: EOptionStaticIds.AllAbove }));
              }
            }}
          />
        </UneditableInfoTooltip>
        <HubertLabel htmlFor="create-question-option-switch-all-above" className={clsx('whitespace-nowrap')}>
          {t('selects.allOfAbove')}
        </HubertLabel>
      </div>

      <div className={clsx('hubert-vertical-center flex flex-row justify-between px-4')}>
        <UneditableInfoTooltip disabled={isTemplateEdit}>
          <HubertSwitch
            id="create-question-option-switch-none-above"
            disabled={isTemplateEdit}
            checked={optionExistsById(options ?? [], 'none-above')}
            onCheckedChange={(e) => {
              if (e) {
                dispatch(
                  addStaticOptionAction({ questionId, id: EOptionStaticIds.NoneAbove, language: questionLanguage }),
                );
              } else {
                dispatch(removeOptionById({ questionId, id: EOptionStaticIds.NoneAbove }));
              }
            }}
          />
        </UneditableInfoTooltip>
        <HubertLabel htmlFor="create-question-option-switch-none-above" className={clsx('whitespace-nowrap')}>
          {t('selects.noneOfAbove')}
        </HubertLabel>
      </div>

      <div className={clsx('hubert-vertical-center flex flex-row justify-between px-4')}>
        <UneditableInfoTooltip disabled={isTemplateEdit}>
          <HubertSwitch
            id="create-question-option-switch-other"
            disabled={isTemplateEdit}
            checked={optionExistsById(options ?? [], 'other')}
            onCheckedChange={(e) => {
              if (e) {
                dispatch(addStaticOptionAction({ questionId, id: EOptionStaticIds.Other, language: questionLanguage }));
              } else {
                dispatch(removeOptionById({ questionId, id: EOptionStaticIds.Other }));
              }
            }}
          />
        </UneditableInfoTooltip>
        <HubertLabel htmlFor="create-question-option-switch-other" className={clsx('whitespace-nowrap')}>
          {t('selects.otherDontKnow')}
        </HubertLabel>
      </div>

      <div className={clsx('hubert-vertical-center flex flex-row justify-between px-4')}>
        <HubertLabel
          className={clsx('pr-4 whitespace-nowrap', isReadOnly && 'opacity-50')}
          htmlFor="create-question-option-switch-minimum"
        >
          {t('selects.selectMin')}
        </HubertLabel>
        <DebouncedInput
          disabled={isReadOnly}
          id="create-question-option-switch-minimum"
          type="number"
          value={minInputValue}
          onChange={handleMinChange}
          placeholder={placeholderMin}
          min={0}
          debounce={300}
        />
      </div>

      <div className={clsx('hubert-vertical-center flex flex-row justify-between px-4')}>
        <HubertLabel
          className={clsx('pr-4 whitespace-nowrap', isReadOnly && 'opacity-50')}
          htmlFor="create-question-option-switch-maximum"
        >
          {t('selects.selectMax')}
        </HubertLabel>
        <DebouncedInput
          disabled={isReadOnly}
          id="create-question-option-switch-maximum"
          type="number"
          value={maxInputValue}
          onChange={handleMaxChange}
          placeholder={placeholderMax}
          min={0}
          debounce={300}
        />
      </div>
    </div>
  );
};

export default CreateOptionOptions;
